import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, FileTextIcon, DownloadIcon, EditIcon, FileIcon, FileCheck, Receipt, FileUser, EyeIcon, TrashIcon } from 'lucide-react';
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardContent,
  Button,
  toast,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@etherisc/ui-kit';
import { quoteService } from '../../services/quoteService';
import { apiClient } from '../../services/api';
import type { Quote } from '../../types/quote';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { extractErrorMessage } from '../../utils/errorUtils';

import type { GeneratedDocument } from '../../types/documentTemplate';

// Extended type for UI purposes
interface UIGeneratedDocument extends GeneratedDocument {
  type: 'quote' | 'policy' | 'invoice';
}

export function QuoteDocumentManagement() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [documentError, setDocumentError] = useState<{
    title: string;
    message: string;
    details?: string[];
    suggestion?: string;
  } | null>(null);
  const [generatedDocuments, setGeneratedDocuments] = useState<UIGeneratedDocument[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);

  // Load generated documents for the quote
  const loadGeneratedDocuments = async () => {
    if (!id) return;
    
    try {
      setDocumentsLoading(true);
      const response = await apiClient.getGeneratedDocuments(id);
      
      // Transform documents to include type based on template or filename
      const documentsWithType: UIGeneratedDocument[] = response.documents.map(doc => ({
        ...doc,
        type: determineDocumentType(doc)
      }));
      
      setGeneratedDocuments(documentsWithType);
    } catch (err) {
      console.error('Error loading generated documents:', err);
      setGeneratedDocuments([]);
      toast.error('Failed to load generated documents', {
        description: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setDocumentsLoading(false);
    }
  };

  // Helper function to determine document type from filename or template
  const determineDocumentType = (doc: GeneratedDocument): 'quote' | 'policy' | 'invoice' => {
    const fileName = doc.fileName.toLowerCase();
    if (fileName.includes('quote')) return 'quote';
    if (fileName.includes('policy')) return 'policy';
    if (fileName.includes('invoice')) return 'invoice';
    return 'quote'; // default
  };

  // Load quote data
  useEffect(() => {
    const loadQuote = async () => {
      if (!id) {
        setError('Quote ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await quoteService.getQuoteById(id);
        setQuote(response.quote);
        setError(null);
        
        // Load generated documents after quote is loaded
        await loadGeneratedDocuments();
      } catch (err) {
        console.error('Error loading quote:', err);
        setError('Failed to load quote details');
        toast.error('Failed to load quote', {
          description: 'Please try again or contact support if the problem persists.'
        });
      } finally {
        setLoading(false);
      }
    };

    loadQuote();
  }, [id]);

  const handleBack = () => {
    navigate('/admin/quotes');
  };

  const handleDownloadJson = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const comprehensiveData = await quoteService.getComprehensiveQuoteById(id);
      
      // The API client unwraps the response, so comprehensiveData is the actual data
      // Check if we have the expected data structure
      if (!comprehensiveData || !comprehensiveData.quote) {
        throw new Error('No comprehensive quote data received from server');
      }
      
      // Create and download the JSON file
      const jsonData = JSON.stringify(comprehensiveData, null, 2);
      const blob = new Blob([jsonData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `quote-${quote?.quoteNumber || id}-comprehensive.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Quote data downloaded successfully');
    } catch (error) {
      console.error('Error downloading quote data:', error);
      toast.error('Failed to download quote data', {
        description: 'Please try again or contact support if the problem persists.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    if (!id) return;

    try {
      setLoading(true);

      // Call the backend template endpoint and get blob + filename
      const { blob, filename } = await apiClient.downloadQuoteTemplate(id, 'simplified');

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('Error downloading template:', error);
      toast.error('Failed to download template', {
        description: error instanceof Error ? error.message : 'Please try again or contact support if the problem persists.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateQuoteDocument = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setDocumentError(null); // Clear any previous errors
      const result = await apiClient.generateQuoteDocument(id);
      
      if (result.success && result.pdfUrl) {
        // Open the PDF in a new tab
        window.open(result.pdfUrl, '_blank');
        toast.success('Quote document generated successfully');
        
        // Refresh the documents list
        await loadGeneratedDocuments();
      } else {
        throw new Error('Failed to generate quote document');
      }
    } catch (error) {
      console.error('Error generating quote document:', error);
      const errorInfo = extractErrorMessage(error);
      setDocumentError(errorInfo);
      
      // Also show a toast for immediate feedback
      toast.error(errorInfo.title, {
        description: errorInfo.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePolicyDocument = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setDocumentError(null); // Clear any previous errors
      const result = await apiClient.generatePolicyDocument(id);
      
      if (result.success && result.pdfUrl) {
        // Open the PDF in a new tab
        window.open(result.pdfUrl, '_blank');
        toast.success('Policy document generated successfully');
        
        // Refresh the documents list
        await loadGeneratedDocuments();
      } else {
        throw new Error('Failed to generate policy document');
      }
    } catch (error) {
      console.error('Error generating policy document:', error);
      const errorInfo = extractErrorMessage(error);
      setDocumentError(errorInfo);
      
      // Also show a toast for immediate feedback
      toast.error(errorInfo.title, {
        description: errorInfo.message
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoiceDocument = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setDocumentError(null); // Clear any previous errors
      const result = await apiClient.generateInvoiceDocument(id);
      
      if (result.success && result.pdfUrl) {
        // Open the PDF in a new tab
        window.open(result.pdfUrl, '_blank');
        toast.success('Invoice document generated successfully');
        
        // Refresh the documents list
        await loadGeneratedDocuments();
      } else {
        throw new Error('Failed to generate invoice document');
      }
    } catch (error) {
      console.error('Error generating invoice document:', error);
      const errorInfo = extractErrorMessage(error);
      setDocumentError(errorInfo);
      
      // Also show a toast for immediate feedback
      toast.error(errorInfo.title, {
        description: errorInfo.message
      });
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for document management
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewDocument = (document: UIGeneratedDocument) => {
    try {
      // Use the document's fileUrl to open in a new tab
      if (document.fileUrl) {
        window.open(document.fileUrl, '_blank');
      } else {
        toast.error('Document URL not available');
      }
    } catch (error) {
      console.error('Error viewing document:', error);
      toast.error('Failed to open document');
    }
  };

  const handleDownloadDocument = async (doc: UIGeneratedDocument) => {
    try {
      if (!doc.fileUrl) {
        toast.error('Document URL not available');
        return;
      }

      const response = await fetch(doc.fileUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch document: ${response.statusText}`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.originalFileName || doc.fileName;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (document: UIGeneratedDocument) => {
    try {
      // Show confirmation dialog
      if (!window.confirm(`Are you sure you want to delete "${document.fileName}"? This action cannot be undone.`)) {
        return;
      }

      // Delete the document
      await apiClient.deleteDocument(document.id);
      
      // Refresh the documents list
      await loadGeneratedDocuments();
      
      toast.success('Document deleted successfully');
    } catch (error) {
      console.error('Error deleting document:', error);
      toast.error('Failed to delete document');
    }
  };

  const filterDocumentsByType = (type: 'quote' | 'policy' | 'invoice'): UIGeneratedDocument[] => {
    return generatedDocuments.filter(doc => doc.type === type);
  };

  // Helper function to get document count by type
  const getDocumentCountByType = (type: 'quote' | 'policy' | 'invoice'): number => {
    return generatedDocuments.filter(doc => doc.type === type).length;
  };

  const renderDocumentsTable = (documents: UIGeneratedDocument[]) => {
    if (documents.length === 0) {
      return (
        <div className="text-center py-8">
          <FileTextIcon size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 mb-2">No documents available</p>
          <p className="text-sm text-gray-500">
            Generated documents will appear here
          </p>
        </div>
      );
    }

    return (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Created At</TableHead>
            <TableHead>Size</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((document) => (
            <TableRow key={document.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <FileTextIcon size={16} className="text-gray-400" />
                  <span className="font-medium">{document.originalFileName || document.fileName}</span>
                </div>
              </TableCell>
              <TableCell className="text-gray-600">
                {formatDate(document.createdAt)}
              </TableCell>
              <TableCell className="text-gray-600">
                {formatFileSize(document.fileSize)}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleViewDocument(document)}
                    className="h-8 w-8 p-0"
                    title="View document"
                  >
                    <EyeIcon size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadDocument(document)}
                    className="h-8 w-8 p-0"
                    title="Download document"
                  >
                    <DownloadIcon size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteDocument(document)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                    title="Delete document"
                  >
                    <TrashIcon size={14} />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10 rounded" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>

        {/* Content Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-96 w-full rounded-lg" />
          <Skeleton className="h-96 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (error || !quote) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon size={16} />
            Back to Quotes
          </Button>
          
          {id && (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                className="flex items-center gap-2"
                disabled={loading}
                title="Download markdown template with Jinja2 syntax"
              >
                <FileIcon size={16} />
                {loading ? 'Downloading...' : 'Download Template'}
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadJson}
                className="flex items-center gap-2"
                disabled={loading}
                title="Download comprehensive quote data as JSON file"
              >
                <DownloadIcon size={16} />
                {loading ? 'Downloading...' : 'Download JSON'}
              </Button>
            </div>
          )}
        </div>

        <Card>
          <CardContent className="p-8 text-center">
            <div className="text-red-600 mb-4">
              <FileTextIcon size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {error || 'Quote not found'}
            </h3>
            <p className="text-gray-600 mb-4">
              Unable to load the quote document management page.
            </p>
            <Button onClick={handleBack} variant="outline">
              Return to Quotes
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleBack}
            className="flex items-center gap-2"
          >
            <ArrowLeftIcon size={16} />
            Back to Quotes
          </Button>
          
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Quote Documents
            </h1>
            <p className="text-gray-600">
              Manage documents for Quote #{quote.quoteNumber}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadTemplate}
            className="flex items-center gap-2"
            disabled={loading}
            title="Download markdown template with Jinja2 syntax"
          >
            <FileIcon size={16} />
            {loading ? 'Downloading...' : 'Download Template'}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadJson}
            className="flex items-center gap-2"
            disabled={loading}
            title="Download comprehensive quote data as JSON file"
          >
            <DownloadIcon size={16} />
            {loading ? 'Downloading...' : 'Download JSON'}
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/quotes/${id}/edit`)}
            className="flex items-center gap-2"
          >
            <EditIcon size={16} />
            Edit
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {documentError && (
        <ErrorDisplay
          title={documentError.title}
          message={documentError.message}
          details={documentError.details}
          suggestion={documentError.suggestion}
          onDismiss={() => setDocumentError(null)}
        />
      )}

      {/* Document Generation Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon size={20} />
            Generate Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              onClick={handleGenerateQuoteDocument}
              disabled={loading}
              className="flex items-center gap-2 h-12"
              variant="outline"
            >
              <FileUser size={20} />
              <div className="text-left">
                <div className="font-medium">Generate Quote</div>
                <div className="text-xs text-gray-500">PDF document with quote details</div>
              </div>
            </Button>
            
            <Button
              onClick={handleGeneratePolicyDocument}
              disabled={loading}
              className="flex items-center gap-2 h-12"
              variant="outline"
            >
              <FileCheck size={20} />
              <div className="text-left">
                <div className="font-medium">Generate Policy</div>
                <div className="text-xs text-gray-500">PDF policy document</div>
              </div>
            </Button>
            
            <Button
              onClick={handleGenerateInvoiceDocument}
              disabled={loading}
              className="flex items-center gap-2 h-12"
              variant="outline"
            >
              <Receipt size={20} />
              <div className="text-left">
                <div className="font-medium">Generate Invoice</div>
                <div className="text-xs text-gray-500">PDF invoice document</div>
              </div>
            </Button>
          </div>
          
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Documents are generated using templates with the following tag combinations:
            </p>
            <ul className="text-sm text-blue-700 mt-1 ml-4 list-disc">
              <li>Quote documents use templates tagged with <code className="bg-blue-100 px-1 rounded">quote</code> + <code className="bg-blue-100 px-1 rounded">doc</code></li>
              <li>Policy documents use templates tagged with <code className="bg-blue-100 px-1 rounded">policy</code> + <code className="bg-blue-100 px-1 rounded">doc</code></li>
              <li>Invoice documents use templates tagged with <code className="bg-blue-100 px-1 rounded">invoice</code> + <code className="bg-blue-100 px-1 rounded">doc</code></li>
            </ul>
          </div>
        </CardContent>
      </Card>

      {/* Quote Information Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileTextIcon size={20} />
            Quote Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-600">Quote Number</label>
              <p className="text-sm font-mono text-gray-900">{quote.quoteNumber}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Product</label>
              <p className="text-sm text-gray-900">{quote.product?.name || 'N/A'}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-600">Status</label>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                quote.state === 'approved' ? 'bg-green-100 text-green-800' :
                quote.state === 'new' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {quote.state}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Existing Documents Card with Tabs */}
      <Card>
        <CardHeader>
          <CardTitle>Existing Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="quote" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="quote" className="flex items-center gap-2">
                <FileUser size={16} />
                Quote Documents
                {getDocumentCountByType('quote') > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded-full">
                    {getDocumentCountByType('quote')}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="policy" className="flex items-center gap-2">
                <FileCheck size={16} />
                Policy Documents
                {getDocumentCountByType('policy') > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded-full">
                    {getDocumentCountByType('policy')}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="invoice" className="flex items-center gap-2">
                <Receipt size={16} />
                Invoices
                {getDocumentCountByType('invoice') > 0 && (
                  <span className="ml-1 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-600 rounded-full">
                    {getDocumentCountByType('invoice')}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="quote" className="mt-6">
              {documentsLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                renderDocumentsTable(filterDocumentsByType('quote'))
              )}
            </TabsContent>
            
            <TabsContent value="policy" className="mt-6">
              {documentsLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                renderDocumentsTable(filterDocumentsByType('policy'))
              )}
            </TabsContent>
            
            <TabsContent value="invoice" className="mt-6">
              {documentsLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                renderDocumentsTable(filterDocumentsByType('invoice'))
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
